// contexts/AuthContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from '../api/axios';
import {jwtDecode} from 'jwt-decode';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // --- ADMIN STATE ---
  const [user, setUser] = useState(null);
  const [hasFilledSchoolInfo, setHasFilledSchoolInfo] = useState(false);
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // --- TEACHER STATE ---
  const [teacherUser, setTeacherUser] = useState(null);
  const [teacherLoading, setTeacherLoading] = useState(true);
  const [isTeacherAuthenticated, setIsTeacherAuthenticated] = useState(false);

  // --- FETCH SCHOOL INFO (ADMIN) ---
  const fetchSchoolInfo = useCallback(async () => {
    try {
      const response = await axios.get('/schools');
      setSchoolInfo(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des informations de l\'école', error);
    }
  }, []);

  // --- CHECK AUTH (ADMIN) ---
  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp < Date.now() / 1000) {
          throw new Error('Token expiré');
        }
        const response = await axios.get('/auth/me'); // l’admin “me”
        setUser(response.data);
        setIsAuthenticated(true);
        setHasFilledSchoolInfo(response.data.hasFilledSchoolInfo);
        if (response.data.hasFilledSchoolInfo) {
          await fetchSchoolInfo();
        }
      } catch {
        localStorage.removeItem('token');
        setUser(null);
        setIsAuthenticated(false);
      }
    } else {
      setUser(null);
      setIsAuthenticated(false);
    }
    setLoading(false);
  }, [fetchSchoolInfo]);

  const [teacherToken, setTeacherToken] = useState(
      () => localStorage.getItem('teacherToken')
   );

  // --- CHECK AUTH (TEACHER) ---
  const checkTeacherAuth = useCallback(async () => {
    const token = localStorage.getItem('teacherToken');
    setTeacherToken(token);
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp < Date.now() / 1000) {
          throw new Error('Teacher token expiré');
        }
        const response = await axios.get('/teacher/auth/me', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTeacherUser(response.data.teacher);
        setIsTeacherAuthenticated(true);
      } catch {
        localStorage.removeItem('teacherToken');
        setTeacherUser(null);
        setIsTeacherAuthenticated(false);
      }
    } else {
      setTeacherUser(null);
      setIsTeacherAuthenticated(false);
    }
    setTeacherLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
    checkTeacherAuth();
  }, [checkAuth, checkTeacherAuth]);

  // --- ADMIN LOGIN / LOGOUT ---
  const login = async (phoneNumber, password) => {
    const response = await axios.post('/auth/login', { phoneNumber, password });
    localStorage.setItem('token', response.data.token);
    setUser(response.data.user);
    setIsAuthenticated(true);
    setHasFilledSchoolInfo(response.data.user.hasFilledSchoolInfo);
    if (response.data.user.hasFilledSchoolInfo) {
      await fetchSchoolInfo();
    }
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
    setHasFilledSchoolInfo(false);
    setSchoolInfo(null);
  };

  // --- TEACHER REGISTER / LOGIN / LOGOUT ---
  const registerTeacher = async ({ phoneNumber, password }) => {
    try {
      const res = await axios.post('/teacher/auth/register', { phoneNumber, password });
      // on ne connecte pas automatiquement, on renvoie juste le succès
      return { success: true, message: res.data.message };
    } catch (error) {
      console.error('Échec de l\'inscription teacher', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur inscription'
      };
    }
  };

  const loginTeacher = async (phoneNumber, password) => {
    try {
      const res = await axios.post('/teacher/auth/login', { phoneNumber, password });
      const { token, teacher } = res.data;
      localStorage.setItem('teacherToken', token);
      setTeacherUser(res.data.teacher);
      setTeacherToken(token);
      setIsTeacherAuthenticated(true);
      return { success: true };
    } catch (error) {
      console.error('Échec de la connexion teacher', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur login',
        code: error.response?.data?.code
      };
    }
  };

  const logoutTeacher = () => {
    localStorage.removeItem('teacherToken');
    setTeacherUser(null);
    setIsTeacherAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{
      // admin
      user,
      loading,
      isAuthenticated,
      hasFilledSchoolInfo,
      schoolInfo,
      login,
      logout,
      checkAuth,
      fetchSchoolInfo,
      setHasFilledSchoolInfo,
      // teacher
      teacherUser,
      teacherLoading,
      isTeacherAuthenticated,
      registerTeacher,
      loginTeacher,
      logoutTeacher,
      checkTeacherAuth,
      teacherToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};
